import React from 'react';
import { perform3DSMethod } from '@/lib/utils/threeDSUtils';
import { log3DSFormEvent } from '@/lib/utils/threeDSLogging';


const use3DSAuthentication = ({
    showForm,
    setDisplayDoNotLeaveWarning,
    iframe3ds,
    iframe3dsmethod,
    showSpinner,
    lyriaUrl,
    paymentRequestId,
    handleSuccess,
    handleError,
    force3dsRedirect = false,
}) => {
    const [is3DSIframeShown, setIs3DSIframeShown] = React.useState(false);
    const timeoutHandleRef = React.useRef(null);
    
    // The function to check the enrollment of the card when 3DS method happens.
    const verifyEnrollment = React.useCallback(
        (verifyParameters, automatic_3ds) => {
            showForm(false);
            setDisplayDoNotLeaveWarning(true);
            let iframe;
            if (automatic_3ds && verifyParameters.code === automatic_3ds) {
                iframe = iframe3dsmethod.current;
                log3DSFormEvent({
                    lyriaUrl,
                    paymentRequestId,
                    formApiUrl: verifyParameters.url,
                    eventType: 'acs_iframe_redirect',
                });
                perform3DSMethod(iframe, verifyParameters.method, verifyParameters.url, verifyParameters.data);
                showSpinner(true);
                setIs3DSIframeShown(false);
            } else {
                if (force3dsRedirect) {
                    try {
                        log3DSFormEvent({
                            lyriaUrl,
                            paymentRequestId,
                            formApiUrl: verifyParameters.url,
                            eventType: 'acs_page_redirect',
                        });
                        perform3DSMethod(document, verifyParameters.method, verifyParameters.url, verifyParameters.data);
                    } catch (error) {
                        log3DSFormEvent({
                            lyriaUrl,
                            paymentRequestId,
                            formApiUrl: verifyParameters.url,
                            eventType: 'js_error',
                            errorMessage: error.message,
                            jsStackTrace: error.stack,
                        });
                        throw error;
                    }
                    showSpinner(false);
                } else {
                    iframe = iframe3ds.current;
                    setIs3DSIframeShown(true);
                    if (!iframe) {
                        // Avoid sentry noise: but to investigate why the reference is null (maybe a rerender)
                        console.error('Impossible to retrieve iframe');
                        return;
                    }

                    if (timeoutHandleRef.current !== null) {
                        clearTimeout(timeoutHandleRef.current);
                        timeoutHandleRef.current = null;
                    }

                    const submitTime = Date.now();

                    iframe.onload = () => {
                        const elapsed = Date.now() - submitTime;
                        if (timeoutHandleRef.current !== null) {
                            clearTimeout(timeoutHandleRef.current);
                            timeoutHandleRef.current = null;
                        }
                        log3DSFormEvent({
                            lyriaUrl,
                            paymentRequestId,
                            formApiUrl: verifyParameters.url,
                            eventType: 'iframe_loaded',
                            elapsedMs: elapsed,
                        });
                        iframe.onload = () => {
                            log3DSFormEvent({
                                lyriaUrl,
                                paymentRequestId,
                                formApiUrl: verifyParameters.url,
                                eventType: 'iframe_redirected',
                                elapsedMs: Date.now() - submitTime,
                            });
                            iframe.onload = null;
                        };
                    };

                    try {
                        log3DSFormEvent({
                            lyriaUrl,
                            paymentRequestId,
                            formApiUrl: verifyParameters.url,
                            eventType: 'acs_iframe_redirect',
                        });
                        perform3DSMethod(iframe, verifyParameters.method, verifyParameters.url, verifyParameters.data);
                    } catch (error) {
                        log3DSFormEvent({
                            lyriaUrl,
                            paymentRequestId,
                            formApiUrl: verifyParameters.url,
                            eventType: 'js_error',
                            errorMessage: error.message,
                            jsStackTrace: error.stack,
                        });
                        throw error;
                    }

                    showSpinner(false);
                    iframe.style.display = 'block';

                    timeoutHandleRef.current = setTimeout(() => {
                        timeoutHandleRef.current = null;
                        iframe.onload = null;
                        log3DSFormEvent({
                            lyriaUrl,
                            paymentRequestId,
                            formApiUrl: verifyParameters.url,
                            eventType: 'timeout',
                            elapsedMs: 30_000,
                        });
                    }, 30_000);
                }
            }
        },
        [showForm, setDisplayDoNotLeaveWarning, iframe3dsmethod, showSpinner, force3dsRedirect, iframe3ds, lyriaUrl, paymentRequestId],
    );

    // The message handler (to manage iframes)
    const postMessageHandler = React.useCallback(
        (message) => {
            const data = message && message.data;
            if (message.origin !== lyriaUrl) {
                return;
            }
            if (!data || typeof data !== 'object' || typeof data.status !== 'string') {
                return;
            }
            if (message.data.status === 'success') {
                if (timeoutHandleRef.current !== null) {
                    clearTimeout(timeoutHandleRef.current);
                    timeoutHandleRef.current = null;
                }
                if (iframe3ds.current) {
                    iframe3ds.current.onload = null;
                }
                setIs3DSIframeShown(false);
                handleSuccess();
            } else if (message.data.status === 'error') {
                if (timeoutHandleRef.current !== null) {
                    clearTimeout(timeoutHandleRef.current);
                    timeoutHandleRef.current = null;
                }
                if (iframe3ds.current) {
                    iframe3ds.current.onload = null;
                }
                setIs3DSIframeShown(false);
                handleError(message.data.data);
            } else if (message.data.status === 'threedsmethod') {
                setIs3DSIframeShown(false);
                verifyEnrollment(message.data);
            }
        },
        [handleError, handleSuccess, iframe3ds, lyriaUrl, verifyEnrollment],
    );

    // Clear the 3DS iframe timeout on unmount to avoid stale callbacks.
    React.useEffect(
        () => () => {
            if (timeoutHandleRef.current !== null) {
                clearTimeout(timeoutHandleRef.current);
            }
        },
        [],
    );

    // The side effect to add a listener of message posted by Payline.
    React.useEffect(
        () => {
            window.addEventListener('message', postMessageHandler);
            return () => window.removeEventListener('message', postMessageHandler);
        },
        [postMessageHandler],
    );

    return {
        is3DSIframeShown,
        verifyEnrollment,
    };
};

export default use3DSAuthentication;
